import { FC, useState } from "react";
import { Loader2 } from "lucide-react";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import useImpersonationTargets from "src/features/Permissions/hooks/useImpersonationTargets";
import useAddImpersonationTarget from "src/features/Permissions/hooks/useAddImpersonationTarget";
import useRemoveImpersonationTarget from "src/features/Permissions/hooks/useRemoveImpersonationTarget";
import EmployeeSearch from "src/features/reporting/components/EmployeeSearch/EmployeeSearch";
import ImpersonationTargetCard from "./ImpersonationTargetCard";

interface Props {
	impersonatorUserId: string;
}

const ImpersonationSection: FC<Props> = ({ impersonatorUserId }) => {
	const [pendingRemove, setPendingRemove] =
		useState<AdminImpersonationTarget | null>(null);
	// Bumped after each successful add so the search input remounts with a
	// fresh state — clears the field and any leftover dropdown.
	const [pickerResetKey, setPickerResetKey] = useState(0);

	const { data: targets, isLoading } =
		useImpersonationTargets(impersonatorUserId);
	const { mutate: addTarget, isLoading: isAdding } =
		useAddImpersonationTarget(impersonatorUserId);
	const { mutate: removeTarget, isLoading: isRemoving } =
		useRemoveImpersonationTarget(impersonatorUserId);

	const excluded = new Set<string>([
		impersonatorUserId,
		...(targets?.map((t) => t.user_id) ?? [])
	]);

	const handlePick = (employee: { id: string }) => {
		// Guard against rapid double-selection before the targets query
		// refreshes — the mutation hasn't yet added the user to `excluded`,
		// so the picker would otherwise allow another POST for them.
		if (isAdding) return;
		addTarget(
			{ impersonatorUserId, targetUserId: employee.id },
			{ onSuccess: () => setPickerResetKey((k) => k + 1) }
		);
	};

	const handleConfirmRemove = () => {
		if (!pendingRemove) return;
		removeTarget(
			{ impersonatorUserId, targetUserId: pendingRemove.user_id },
			{
				onSuccess: () => setPendingRemove(null)
			}
		);
	};

	return (
		<div className="rounded-xl border border-border bg-subtle shadow-sm p-6">
			<p className="font-heading text-lg font-medium text-foreground mb-1 block">
				Impersonation
			</p>
			<p className="text-sm text-muted-foreground mb-4">
				Users this person can impersonate in the social feed.
			</p>

			{isLoading ? (
				<div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
					<Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
					Loading targets…
				</div>
			) : targets && targets.length > 0 ? (
				<div className="flex flex-col gap-2 mb-4">
					{targets.map((target) => (
						<ImpersonationTargetCard
							key={target.user_id}
							target={target}
							onRemove={() => setPendingRemove(target)}
							isRemoving={
								isRemoving && pendingRemove?.user_id === target.user_id
							}
						/>
					))}
				</div>
			) : (
				<p className="text-sm text-muted-foreground mb-4">
					No impersonation targets assigned yet.
				</p>
			)}

			{/* Visual heading only — EmployeeSearch has its own aria-label. */}
			<p className="text-sm font-medium text-zinc-600 mb-2">Add user</p>
			<EmployeeSearch
				key={pickerResetKey}
				excludedUserIds={excluded}
				onSelect={handlePick}
			/>

			<ConfirmationModal
				isOpen={pendingRemove !== null}
				title="Remove impersonation target?"
				description={
					pendingRemove
						? `Remove ${pendingRemove.first_name} ${pendingRemove.last_name} from this user's impersonation targets?`
						: ""
				}
				onCancel={() => {
					if (!isRemoving) setPendingRemove(null);
				}}
				cancelBtnProps={{ disabled: isRemoving }}
				onSubmit={handleConfirmRemove}
				submitBtnProps={{ isLoading: isRemoving }}
			/>
		</div>
	);
};

export default ImpersonationSection;
